// lib/promanity/onboarding/buildOnboardingSteps.ts

import type {
  NextStep,
  PromanityExperienceData,
} from '@/lib/promanity/types'

export const PHASE_1_STEP_IDS = [
  'ambassador-license',
  'wallet-setup',
  'promanity-portal',
] as const

const ONBOARDING_STEP_COPY: Record<
  string,
    {
        title: string
        description: string
        ctaLabel: string
    } 
  > = {
  'ambassador-license': {
    title: 'Activa tu acceso',
    description:
      'Da el primer paso para compartir Promanity y comenzar a construir tu red.',
    ctaLabel: 'Activar acceso',
  },

  'wallet-setup': {
    title: 'Elige donde recibirás tus ganancias',
    description:
      'Conecta la wallet donde quieres recibir las recompensas que generes.',
    ctaLabel: 'Configurar wallet',
  },

  'promanity-portal': {
    title: 'Prepara tu enlace personal',
    description:
      'Crea el enlace que podrás compartir con las personas que quieras invitar.',
    ctaLabel: 'Crear mi enlace',
  },

  'strategic-profile': {
    title: 'Cuéntame qué quieres lograr',
    description: 'Ayúdame a entender tus prioridades, lo que hoy te está frenando y hacia donde quieres avanzar.',
    ctaLabel: 'Contarte sobre mí',
  },

  'knowledge-base': {
    title: 'Compárteme lo que ya sabes',
    description: 'Agrega información sobre tus ideas, productos, servicios o proyectos para que podamos trabajar con más contexto.',
    ctaLabel: 'Compartir información',
  },

  'operational-directives': {
    title: 'Dime cómo te gusta trabajar',
    description: 'Elige cuándo quieres que te avise, cómo prefieres recibir recomendaciones y qué debo hacer cuando algo no esté claro.',
    ctaLabel: 'Elegir cómo trabajaremos',
  },

  'assistant-identity': {
    title: 'Dale una voz a AIA',
    description: 'Elige cómo quieres que AIA se exprese y se comunique contigo.',
    ctaLabel: 'Elegir su voz',
  },

  'avatar-identity': {
    title: 'Elige cómo quieres presentarte',
    description:
      'Define la imagen que podrá representarte cuando creemos experiencias y contenido.',
    ctaLabel: 'Elegir mi imagen',
  },
}

export type BuildOnboardingStepsOptions = {
  dashboard: PromanityExperienceData

  initialJourney?: string | null

  activationAllowed: boolean

  walletAddress: string | null

  portalUrl: string | null

  canBypassPhaseLocks: boolean

  phase2Enabled: boolean

  ambassadorLicenseStep: NextStep

  walletStep: NextStep

  portalStep: NextStep
}

function applyCopy(step: NextStep): NextStep {
  const branded = ONBOARDING_STEP_COPY[step.id]

  if (!branded) return step

  return {
    ...step,
    ...branded,
  }
}

function buildAmbassadorSteps(
  dashboard: PromanityExperienceData,
  ambassadorLicenseStep: NextStep,
  walletStep: NextStep,
  portalStep: NextStep
) {
  const remaining = dashboard.nextSteps.filter(
    (step) =>
      !PHASE_1_STEP_IDS.includes(
        step.id as (typeof PHASE_1_STEP_IDS)[number]
      )
  )

  return [
    ambassadorLicenseStep,
    walletStep,
    portalStep,
    ...remaining,
  ].map(applyCopy)
}

function buildClientSteps(
  dashboard: PromanityExperienceData
) {
  const wantedOrder = [
    'strategic-profile',
    'knowledge-base',
    'operational-directives',
  ]

  const map = new Map(
    dashboard.nextSteps.map((s) => [s.id, s])
  )

  return wantedOrder
    .map((id) => map.get(id))
    .filter(Boolean)
    .map((step) => applyCopy(step as NextStep))
}

export function buildOnboardingSteps({
  dashboard,
  initialJourney,
  ambassadorLicenseStep,
  walletStep,
  portalStep,
  canBypassPhaseLocks,
  phase2Enabled,
}: BuildOnboardingStepsOptions): NextStep[] {
  const base =
    initialJourney === 'ambassador'
      ? buildAmbassadorSteps(
          dashboard,
          ambassadorLicenseStep,
          walletStep,
          portalStep
        )
      : buildClientSteps(dashboard)

  return base.map((step, index) => {
    const isPhase1Step =
      PHASE_1_STEP_IDS.includes(
        step.id as (typeof PHASE_1_STEP_IDS)[number]
      )

    const previousCompleted = base
      .slice(0, index)
      .every((s) => s.done)

    const phaseLocked =
      !canBypassPhaseLocks &&
      !phase2Enabled &&
      !isPhase1Step

    return {
      ...step,

      phaseLocked,

      locked: canBypassPhaseLocks
        ? false
        : phaseLocked ||
          (!step.done && !previousCompleted),
    }
  })
}